import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { data, portal } from "@aawaz/api-client";
import { Card, ErrorBanner, Loading, Notice, Select, SwitchRow, Table } from "@aawaz/ui";
import { useSession } from "../session";
import { useClients } from "../components/ItemPanel";

interface Settings {
  ack: { enabled: boolean };
  chase: { enabled: boolean; days_before: number; template: string };
  nudge: { template: string };
  autopost: { enabled: boolean; min_confidence: number; accounts: Record<string, string> };
  voice: { enabled: boolean };
}
interface Source { binding_id: string; client_org_id: string | null; provider: string; company_name: string | null }

function Toggle({ label, hint, value, onChange }: { label: string; hint: string; value: boolean; onChange: (v: boolean) => void }) {
  return <div className="ui-toggle-row"><SwitchRow label={label} hint={hint} checked={value} onChange={onChange} /></div>;
}

/** Settings → Automation: what AawazAI does on the firm's behalf. */
export function Automation() {
  const ses = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ca-settings"], queryFn: () => data<Settings>(portal.GET("/portal/v1/ca/settings")) });
  const save = useMutation({
    mutationFn: (changes: Record<string, unknown>) => data<Settings>(portal.PUT("/portal/v1/ca/settings", { body: changes })),
    onSuccess: (s) => qc.setQueryData(["ca-settings"], s),
  });
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBanner error={q.error} />;
  const st = q.data;
  return (
    <>
      <ErrorBanner error={save.error} />
      <Card title={ses.has("ca") ? "Talking to your clients" : "Replies on WhatsApp"}>
        <Toggle label="Acknowledge what clients send" value={st.ack.enabled} onChange={(v) => save.mutate({ ack: { enabled: v } })}
          hint="A few seconds after a client sends bills on WhatsApp, they get one reply: what was read, the amount, the month it is filed under, and a request to resend unclear photos." />
        {ses.has("ca") ? <Toggle label="Remind clients about missing documents" value={st.chase.enabled} onChange={(v) => save.mutate({ chase: { enabled: v } })}
          hint={`${st.chase.days_before} days before the GSTR-1 and GSTR-3B due dates, clients still missing documents they usually send get a WhatsApp reminder (template "${st.chase.template}", needs Meta approval).`} /> : null}
        {ses.has("ca") && st.chase.enabled ? (
          <div className="ui-row" style={{ paddingLeft: 28 }}>
            <span className="ui-hint">Days before the due date</span>
            <Select aria-label="Days before" value={String(st.chase.days_before)} onChange={(e) => save.mutate({ chase: { days_before: Number(e.target.value) } })}
                    options={[2, 3, 4, 5, 7].map((d) => ({ value: String(d), label: String(d) }))} />
          </div>
        ) : null}
        <Toggle label="Understand voice notes" value={st.voice.enabled} onChange={(v) => save.mutate({ voice: { enabled: v } })}
          hint={'"Ramesh ke rent bills, pichle mahine ke" files the bills just sent under that client, type and month, and confirms on WhatsApp.'} />
      </Card>
      <AutoPost st={st} onSave={(c) => save.mutate(c)} />
      {save.isSuccess ? <Notice tone="success">Saved.</Notice> : null}
    </>
  );
}

function AutoPost({ st, onSave }: { st: Settings; onSave: (c: Record<string, unknown>) => void }) {
  const clients = useClients();
  const sources = useQuery({ queryKey: ["ca-books-sources"], queryFn: () => data<{ data: Source[] }>(portal.GET("/portal/v1/ca/books/sources")) });
  const zoho = (sources.data?.data ?? []).filter((s) => s.provider === "zoho_books" && s.client_org_id);
  const names = new Map((clients.data?.data ?? []).map((c) => [c.id, c.display_name]));
  return (
    <Card title="Posting into the books">
      <Toggle label="Auto-post verified e-invoices to Zoho Books" value={st.autopost.enabled} onChange={(v) => onSave({ autopost: { enabled: v } })}
        hint="Only purchase e-invoices whose QR signature verified, with the supplier already in Zoho under the same GSTIN, one GST rate, and write-back on for that organization. Everything else waits for a person." />
      {st.autopost.enabled ? (zoho.length === 0 ? <p className="ui-hint">Link a Zoho Books organization to a client first (Channels).</p> : (
        <Table rows={zoho} rowKey={(r) => r.binding_id} columns={[
          { key: "c", header: "Client", render: (r) => names.get(r.client_org_id!) ?? "…" },
          { key: "o", header: "Zoho organization", render: (r) => r.company_name ?? "—" },
          { key: "a", header: "Post to account", render: (r) => <LedgerPick clientId={r.client_org_id!} value={st.autopost.accounts[r.client_org_id!] ?? ""}
            onChange={(acct) => onSave({ autopost: { accounts: { ...st.autopost.accounts, [r.client_org_id!]: acct } } })} /> },
        ]} />)) : null}
    </Card>
  );
}

function LedgerPick({ clientId, value, onChange }: { clientId: string; value: string; onChange: (v: string) => void }) {
  const q = useQuery({ queryKey: ["ca-ledgers", clientId], queryFn: () =>
    data<{ data: { ref: string; name: string; group: string | null }[] }>(portal.GET("/portal/v1/ca/books/ledgers", { params: { query: { client_org_id: clientId } } })) });
  return <Select aria-label="Account" value={value} onChange={(e) => onChange(e.target.value)}
                 options={[{ value: "", label: "Not set (no auto-posting)" }, ...(q.data?.data ?? []).map((l) => ({ value: l.ref, label: l.name }))]} />;
}
